/**
 * Session Monitor Hook
 * Periodically checks if the user's session is still active.
 * If session becomes inactive (logged out from another system), automatically logs out.
 */

import { useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { selectIsAuthenticated } from "../state/selectors";
import { logout } from "../state/slices/authSlice";
import { clearHistory, navigateToView } from "../state/slices/navigationSlice";
import { showNotification } from "../state/slices/notificationsSlice";
import { validateSessionId } from "../services/sessionApi";

const SESSION_CHECK_INTERVAL = 30000; // Check every 30 seconds
const SESSION_CHECK_ON_FOCUS = true; // Check when window regains focus

export function useSessionMonitor() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  const checkSession = useCallback(async () => {
    if (!isAuthenticated || isCheckingRef.current) {
      return;
    }

    // Get session_id from localStorage
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) {
      // No session_id means user hasn't logged in with new flow yet
      // Fallback: don't check (or could use old method)
      return;
    }

    isCheckingRef.current = true;
    try {
      const sessionRes = await validateSessionId(sessionId);
      
      if (sessionRes?.success && !sessionRes.valid) {
        // Session is invalid - user was logged out from another system
        console.warn("[SessionMonitor] Session is invalid - logging out");
        
        // Show notification
        dispatch(
          showNotification({
            message: "You have been logged out because you logged in from another system.",
            type: "error",
          })
        );

        // Logout and redirect to dashboard (login)
        dispatch(logout());
        dispatch(clearHistory());
        dispatch(navigateToView({ view: "dashboard" }));
      }
    } catch (error) {
      // Silently fail - don't interrupt user if check fails
      console.warn("[SessionMonitor] Failed to check session:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear interval if not authenticated
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Initial check on mount
    checkSession();

    // Set up periodic check
    checkIntervalRef.current = setInterval(() => {
      checkSession();
    }, SESSION_CHECK_INTERVAL);

    // Check on window focus (user switches back to tab)
    let focusHandler: (() => void) | null = null;
    if (SESSION_CHECK_ON_FOCUS) {
      focusHandler = () => {
        checkSession();
      };
      window.addEventListener("focus", focusHandler);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (focusHandler) {
        window.removeEventListener("focus", focusHandler);
      }
    };
  }, [isAuthenticated, checkSession]);
}
